import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useTranslation } from "react-i18next";
import { FileCheck, X, Copy, Info } from "lucide-react";
import { Checkbox } from "../ui/checkbox";

/**
 * Dialog to prompt user to save or discard example data when completing preview
 * @typedef {Object} SaveExampleDialogProps
 * @property {boolean} open - Whether the dialog is open
 * @property {(open: boolean) => void} onOpenChange - Callback when dialog open state changes
 * @property {(data: { name: string, description: string, epilogue?: string, shouldCopy?: boolean, isAnonymous?: boolean }) => Promise<void>} onSave - Callback when saving example
 * @property {() => void | Promise<void>} onDiscard - Callback when discarding
 * @property {string} [activityName] - Name of the activity (optional)
 * @property {boolean} [allowCopy=false] - Show option to create editable copy (for Review page)
 * @property {boolean} [showEpilogue=false] - Show epilogue field (only for preview mode)
 * @property {string} [initialEpilogue=''] - Initial epilogue value
 * @property {boolean} [isActivityAuthor=false] - Whether the current user is the activity author
 * @property {boolean} [hideConfirmationCheckboxes=false] - Hide mandatory confirmation checkboxes (e.g. when opening from engagement completion)
 *
 * @param {SaveExampleDialogProps} props
 */
const SaveExampleDialog = ({
  open,
  onOpenChange,
  onSave,
  onDiscard,
  activityName = "",
  allowCopy = false,
  showEpilogue = false,
  initialEpilogue = "",
  isActivityAuthor = false,
  hideConfirmationCheckboxes = false,
}) => {
  const { t } = useTranslation();
  const [exampleName, setExampleName] = useState("");
  const [exampleDescription, setExampleDescription] = useState("");
  const [epilogue, setEpilogue] = useState(initialEpilogue);
  const [isSaving, setIsSaving] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);

  // Mandatory confirmation checkboxes
  const [confirmCheckboxes, setConfirmCheckboxes] = useState({
    removedSensitiveInfo: false,
    reviewedDetails: false,
    allowSharing: false,
  });

  const handleSave = async () => {
    // Validate required fields
    if (!exampleName.trim()) {
      return; // Don't save if name is empty
    }

    setIsSaving(true);
    try {
      await onSave({
        name: exampleName.trim(),
        description: exampleDescription.trim(),
        epilogue: epilogue.trim(),
        shouldCopy: allowCopy, // Always copy when allowCopy is true
        isAnonymous: !isActivityAuthor && isAnonymous, // Only allow anonymous for non-authors
      });
      setExampleName("");
      setExampleDescription("");
      setEpilogue("");
      setIsAnonymous(false);
      setConfirmCheckboxes({
        removedSensitiveInfo: false,
        reviewedDetails: false,
        allowSharing: false,
      });
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = async () => {
    await onDiscard();
    setExampleName("");
    setExampleDescription("");
    setEpilogue("");
    setIsAnonymous(false);
    setConfirmCheckboxes({
      removedSensitiveInfo: false,
      reviewedDetails: false,
      allowSharing: false,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-primary" />
            {allowCopy
              ? t("save_as_example") || "Save as Example"
              : isActivityAuthor
                ? t("mark_as_example") || "Mark as Example"
                : t("propose_as_example") || "Propose as Example"}
          </DialogTitle>
          <DialogDescription>
            {allowCopy
              ? t("save_engagement_as_example_description") ||
                "Save this completed engagement as an example for future reference."
              : isActivityAuthor
                ? t("mark_as_example_description") ||
                  "Mark this engagement as an example that others can view and follow."
                : t("propose_as_example_description") ||
                  "Propose this engagement as an example for the activity author to review."}
          </DialogDescription>
        </DialogHeader>

        {/* {activityName && (
          <div className="text-sm text-muted-foreground">
            <span className="font-medium">{t("activity")}:</span> {activityName}
          </div>
        )} */}

        <div className="py-3 space-y-3">
          <div className="space-y-1.5">
            <Label
              htmlFor="example-name"
              className="flex items-center gap-1 text-xs"
            >
              {t("example_name")} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="example-name"
              placeholder={t("enter_example_name_placeholder")}
              value={exampleName}
              onChange={(e) => setExampleName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !isSaving) {
                  handleSave();
                }
              }}
              className="h-9"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="example-description" className="text-xs">
              {t("example_description")} {t("optional")}
            </Label>
            <Textarea
              id="example-description"
              placeholder={
                t("example_description_guidance_placeholder") ||
                "Explain what makes this a good example: Did it lead to insights? Is it well-documented? Does it show an interesting use case?"
              }
              value={exampleDescription}
              onChange={(e) => setExampleDescription(e.target.value)}
              rows={2}
              className="resize-none text-sm"
            />
          </div>

          {/* Proposal Review Notice - only shown for non-authors */}
          {!isActivityAuthor && (
            <div className="pt-2 space-y-2">
              <div className="flex items-start p-3 space-x-2.5 bg-primary/5 border border-primary/20 rounded-md">
                <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-primary mb-1.5">
                    {t("proposal_review_notice_title") || "About Your Proposal"}
                  </p>
                  <div className="text-[11px] leading-relaxed text-primary space-y-1">
                    <p>
                      {t("proposal_review_notice_1") ||
                        "Your proposal will be reviewed by the activity author, who may approve, edit, or decline it based on their criteria."}
                    </p>
                    <p>
                      {t("proposal_review_notice_2") ||
                        "Not all proposals are published, and that's okay—it doesn't reflect on the quality of your work or effort. We appreciate your contribution!"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Epilogue field - only shown in preview mode */}
          {showEpilogue && (
            <div className="space-y-1.5">
              <Label htmlFor="example-epilogue" className="text-xs">
                {t("epilogue")} ({t("optional")})
              </Label>
              <Textarea
                id="example-epilogue"
                placeholder={
                  t("epilogue_placeholder") ||
                  "What did you learn? What insights did you gain? What would you do differently next time?"
                }
                value={epilogue}
                onChange={(e) => setEpilogue(e.target.value)}
                rows={3}
                className="resize-none text-sm"
              />
              <p className="text-[11px] text-muted-foreground">
                {t("epilogue_description") ||
                  "Reflect on what you learned or accomplished during this engagement"}
              </p>
            </div>
          )}

          {/* Anonymous checkbox - only shown for non-authors */}
          {!isActivityAuthor && (
            <div className="flex items-start space-x-3 pt-2">
              <Checkbox
                id="propose-anonymously"
                checked={isAnonymous}
                onCheckedChange={(checked) => setIsAnonymous(checked === true)}
                className="mt-1"
              />
              <div className="flex-1 space-y-1">
                <Label
                  htmlFor="propose-anonymously"
                  className="text-sm font-medium leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t("propose_anonymously") || "Propose anonymously"}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("propose_anonymously_description") ||
                    "Hide your name when the author reviews this proposal"}
                </p>
              </div>
            </div>
          )}

          {/* Information about edit mode when allowCopy is true */}
          {allowCopy && (
            <div className="pt-2 space-y-2 border-t">
              <div className="flex items-start p-2.5 space-x-2.5 bg-primary/5 border border-primary/20 rounded-md">
                <Copy className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-primary">
                    {t("edit_before_saving") || "Edit before saving"}
                  </p>
                  <p className="text-[11px] leading-snug text-primary mt-1">
                    {t("edit_before_saving_description") ||
                      "Edit the engagement data to remove sensitive information before saving as example"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Mandatory confirmation checkboxes - hidden when opening from engagement completion */}
          {!hideConfirmationCheckboxes && (
            <div className="pt-3 space-y-3 border-t">
              <p className="text-xs font-medium text-gray-700">
                {t("confirm_before_submission") ||
                  "Please confirm the following:"}
              </p>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="removedSensitiveInfo"
                  checked={confirmCheckboxes.removedSensitiveInfo}
                  onCheckedChange={(checked) =>
                    setConfirmCheckboxes({
                      ...confirmCheckboxes,
                      removedSensitiveInfo: checked === true,
                    })
                  }
                  className="mt-0.5"
                />
                <Label
                  htmlFor="removedSensitiveInfo"
                  className="text-xs font-medium leading-tight cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t("confirm_removed_sensitive_info") ||
                    "I have removed all sensitive and personal information"}
                </Label>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="reviewedDetails"
                  checked={confirmCheckboxes.reviewedDetails}
                  onCheckedChange={(checked) =>
                    setConfirmCheckboxes({
                      ...confirmCheckboxes,
                      reviewedDetails: checked === true,
                    })
                  }
                  className="mt-0.5"
                />
                <Label
                  htmlFor="reviewedDetails"
                  className="text-xs font-medium leading-tight cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t("confirm_reviewed_details") ||
                    "I have reviewed all details to ensure they are helpful for others"}
                </Label>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="allowSharing"
                  checked={confirmCheckboxes.allowSharing}
                  onCheckedChange={(checked) =>
                    setConfirmCheckboxes({
                      ...confirmCheckboxes,
                      allowSharing: checked === true,
                    })
                  }
                  className="mt-0.5"
                />
                <Label
                  htmlFor="allowSharing"
                  className="text-xs font-medium leading-tight cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t("confirm_allow_sharing") ||
                    "I agree to share this example with other users"}
                </Label>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleDiscard}
            disabled={isSaving}
            className="gap-2"
          >
            <X className="w-4 h-4" />
            {t("cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={
              isSaving ||
              !exampleName.trim() ||
              (!hideConfirmationCheckboxes &&
                (!confirmCheckboxes.removedSensitiveInfo ||
                  !confirmCheckboxes.reviewedDetails ||
                  !confirmCheckboxes.allowSharing))
            }
            className="gap-2"
          >
            {allowCopy ? (
              <>
                <Copy className="w-4 h-4" />
                {isSaving
                  ? t("creating_copy") || "Creating copy..."
                  : t("continue") || "Continue"}
              </>
            ) : (
              <>
                <FileCheck className="w-4 h-4" />
                {isSaving
                  ? t("saving")
                  : isActivityAuthor
                    ? t("mark_as_example") || "Mark as Example"
                    : t("propose_as_example") || "Propose as Example"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveExampleDialog;
